import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import styles from './TableHead.module.css';
import {
	TableHead as MuiTableHead,
	TableRow,
	TableCell,
	Typography,
	Box,
} from '@mui/material';
import HeaderWithMenu from '../HeaderWithMenu/HeaderWithMenu';
import { pxToRem, responsiveFontSizes } from '../../utils/getFontValue';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { SORT_STATE, SORTED_STRING } from '../../constants';
import moment from 'moment';
import clsx from 'clsx';
import SortIcon from '../../assets/icons/SortIcon';

const TableHead = forwardRef(
  ({
    columns,
    allRows,
    listId,
    rows,
    fieldToFiltersSetMap,
    setFiltersForFieldCurry,
    reorderHeaders,
    noOfFixedColumns,
    isDragDisabled,
    fieldToSearchTextMap,
    setSearchTextForTitleCurry,
    unsortedRows,
    setSortedRows,
    updateTable,
    table,
  }, ref) => {
    const [sortData, setSortData] = useState({
      sortField: null,
      sortState: SORT_STATE.UNSORTED,
    });
   
    // Add initialization ref
    const initialSortRef = useRef(false);

    useEffect(() => {
      // Only run initial sort once when rows are available
      if (rows?.length > 0 && table.sort?.[listId] && !initialSortRef.current) {
        initialSortRef.current = true;
        sortedData();
      }
    }, [rows, table.sort, listId]);

    // Separate effect for subsequent sort updates
    useEffect(() => {
      if (initialSortRef.current && table.sort?.[listId]) {
        sortedData();
      }
    }, [table.sort]);

		function reverseFormatNumber(val, locale) {
			if (typeof val === 'string') {
				const group = new Intl.NumberFormat(locale)
					.format(1111)
					.replace(/1/g, '');
				const decimal = new Intl.NumberFormat(locale)
					.format(1.1)
					.replace(/1/g, '');
				let reversedVal = val
					.toString()
					.replace(new RegExp(`\\${group}`, 'g'), '');
				reversedVal = reversedVal.replace(new RegExp(`\\${decimal}`, 'g'), '.');
				return Number.isNaN(reversedVal) ? 0 : reversedVal;
			}
		}

		function substitueZerosWithPrevValues(formatedRows, field) {
			const updatedRows = [];
			formatedRows.forEach((item) => {
				if (item[field] === 0) {
					unsortedRows.forEach((ele) => {
						if (ele.id === item.id) {
							updatedRows.push(ele);
						}
					});
				} else {
					updatedRows.push(item);
				}
			});
			return updatedRows;
		}
		const numberFormat = (value) => {
			if (value === undefined) return 0;

			return new Intl.NumberFormat('en-IN', {
				// style: 'currency',
				currency: 'INR',
			}).format(value);
		};


		function checkFormat(field) {
			if (
				rows?.filter((item) =>
					moment(item?.[field], 'YYYY-MM-DD', true).isValid(),
				).length > 0 ||
				rows.filter((item) => moment(item?.[field], 'YYYY-DD', true).isValid())
					.length > 0 ||
				rows.filter((item) => moment(item?.[field], 'YYYY-MM', true).isValid())
					.length > 0
			) {
				return 'date';
			}
			if (
				rows
					.map((item) => parseFloat(reverseFormatNumber(item?.[field], 'INR')))
					.filter((item) => !Number.isNaN(item)).length > 0
			) {
				return 'num';
			}
			return 'string';
		}


    const sortedData = useCallback(() => {
      const field = table.sort?.[listId] ? Object.keys(table.sort[listId])[0] : null;
      if (!field || !rows?.length) return;

      const order = table.sort[listId]?.[field];
      if (!order) return;

      const safeString = (value) =>
        typeof value === 'string' ? value.toLowerCase() : '';

      let sortedRows = [...rows];

      switch (order) {
        case SORT_STATE.ASCENDING:
          if (
            checkFormat(field) === 'string' ||
            checkFormat(field) === 'date' ||
            field === SORTED_STRING[field]
          ) {
            sortedRows.sort((a, b) =>
              safeString(a?.[field]) > safeString(b?.[field]) ? 1 : -1
            );
            setSortedRows(sortedRows);
          } else if (checkFormat(field) === 'num') {
            sortedRows.sort((a, b) => a?.[field] - b?.[field]);
            const updatedRows = substitueZerosWithPrevValues(sortedRows, field);
            setSortedRows(updatedRows);
          }
          break;

        case SORT_STATE.DESCENDING:
          if (
            checkFormat(field) === 'string' ||
            checkFormat(field) === 'date' ||
            field === SORTED_STRING[field]
          ) {
            sortedRows.sort((a, b) =>
              safeString(a?.[field]) > safeString(b?.[field]) ? -1 : 1
            );
            setSortedRows(sortedRows);
          } else if (checkFormat(field) === 'num') {
            sortedRows.sort((a, b) => b?.[field] - a?.[field]);
            const updatedRows = substitueZerosWithPrevValues(sortedRows, field);
            const numFormatConverted = updatedRows.map((item) =>
              typeof item?.[field] === 'string'
                ? { ...item }
                : { ...item, [field]: numberFormat(item?.[field]) }
            );
            setSortedRows(numFormatConverted);
          }
          break;

        case SORT_STATE.UNSORTED:
          if (unsortedRows?.length > 0) {
            setSortedRows(unsortedRows);
          }
          break;

        default:
          break;
      }
    }, [rows, table.sort, listId, unsortedRows, setSortedRows]);

		const getNextSortState = (field, sortData) => {
			const sortStates = [
				SORT_STATE.ASCENDING,
				SORT_STATE.DESCENDING,
				SORT_STATE.UNSORTED,
			];
			if (field === sortData.sortField) {
				const currentSortState = sortData.sortState;
				const idx = sortStates.indexOf(currentSortState);
				const nextSortStateIdx = (idx + 1) % sortStates.length;
				return sortStates[nextSortStateIdx];
			}
			return sortStates[0];
		};


		const applySort = (field) => {
			const nextSortState = getNextSortState(field, sortData);
			const safeString = (value) =>
				typeof value === 'string' ? value.toLowerCase() : '';

			switch (nextSortState) {
				case SORT_STATE.ASCENDING:
					if (
						checkFormat(field) === 'string' ||
						checkFormat(field) === 'date' ||
						field === SORTED_STRING[field]
					) {
						const ascSortedRows = [...rows];
						ascSortedRows.sort((a, b) =>
							safeString(a?.[field]) > safeString(b?.[field]) ? 1 : -1,
						);
						if (ascSortedRows.length > 0) setSortedRows(ascSortedRows);
					} else if (checkFormat(field) === 'num') {
						rows.sort((a, b) => a?.[field] - b?.[field]);
						const updatedRows = substitueZerosWithPrevValues(rows, field);
						if (updatedRows.length > 0) setSortedRows(updatedRows);
					}
					break;

				case SORT_STATE.DESCENDING:
					if (
						checkFormat(field) === 'string' ||
						checkFormat(field) === 'date' ||
						field === SORTED_STRING[field]
					) {
						const decSortedRows = [...rows];
						decSortedRows.sort((a, b) =>
							a[field]?.toLowerCase() > b[field]?.toLowerCase() ? 1 : -1,
						);
						decSortedRows.reverse();
						if (decSortedRows.length > 0) setSortedRows(decSortedRows);
					} else if (checkFormat(field) === 'num') {
						rows.sort((a, b) => b?.[field] - a?.[field]);
						const updatedRows = substitueZerosWithPrevValues(rows, field);
						const numFormatConverted = updatedRows.map((item) =>
							typeof item?.[field] === 'string'
								? { ...item }
								: { ...item, [field]: numberFormat(item?.[field]) },
						);
						if (numFormatConverted.length > 0)
							setSortedRows(numFormatConverted);
					}
					break;

				case SORT_STATE.UNSORTED:
					if (unsortedRows.length > 0) setSortedRows(unsortedRows);
					break;

				default:
					break;
			}

			updateTable({
				...table,
				sort: {
					[listId]: {
						[field]: nextSortState,
					},
				},
			});

			setSortData({ sortState: nextSortState, sortField: field });
		};


		const renderSortingHeader = (col) => {
			const field = col.field; // Dynamic field name

			const keys =
				table.sort && table.sort[listId]
					? Object.keys(table?.sort[listId])
					: [];

			let sortIcon = <SortIcon className={styles.sortIcon} isAscending={false} />;
			let isActive = false;
			
			if (keys.includes(col.field)) {
				isActive = true;
				if (table.sort[listId]?.[field] === SORT_STATE.ASCENDING) {
					sortIcon = (
						<SortIcon
							className={clsx(styles.sortIcon, styles.openSortIcon)}
							isAscending={true}
						/>
					);
				} else if (table.sort[listId]?.[field] === SORT_STATE.DESCENDING) {
					sortIcon = (
						<SortIcon
							className={clsx(styles.sortIcon, styles.openSortIcon)}
							isAscending={false}
						/>
					);
				}
			}
			return (
				// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
				<span
					className={styles.headerWithMenu}
					onClick={() => applySort(col.field)}
				>
					{sortIcon}
				</span>
			);
		};
		const renderHeader = (col) => {
			if (col.renderHeader) return col.renderHeader(rows);
			if (col.enableSearch || col.enableFilter)
				return (
					<HeaderWithMenu
						column={col}
						selectedFilterOptions={fieldToFiltersSetMap[col.field]}
						applyFilters={setFiltersForFieldCurry(col.field)}
						searchText={fieldToSearchTextMap[col.field]}
						handleSearchTextChange={setSearchTextForTitleCurry(col.field)}
						sortState={sortData.sortState}
						applySort={applySort}
						renderSortingHeader={renderSortingHeader}
						rows={allRows}
						field={col.field}
					/>
				);
			if (col.sortable) return renderSortingHeader(col);
			return (
				<span style={{ paddingBottom: 2, display: 'inline-block' }}>
					{col.title}
				</span>
			);
		};

		return (
			<DragDropContext onDragEnd={reorderHeaders}>
				<Droppable droppableId="droppable" direction="horizontal">
					{(provided) => (
						<MuiTableHead
							ref={(refVal) => {
								provided.innerRef(refVal);
								if (ref != null) ref.current = refVal;
							}}
							{...provided.droppableProps}
							component="div"
							sx={{
								display: 'flex',
								flexDirection:'row',
								justifyContent:'space-between',
								width: '100%',
								overflow: 'hidden',
								position: 'relative',
								backgroundColor: '#F8F8F8',
								'[data-theme="dark"] &': {
									backgroundColor: '#2B2E33',
								},
							}}
						>
							<TableRow component="div">
								{columns?.map((col, index) => (
									<Draggable
										key={col.field}
										draggableId={col.field}
										index={index}
										isDragDisabled={index < noOfFixedColumns || isDragDisabled}
									>
										{(provided) => (
											<TableCell
												ref={provided.innerRef}
												{...provided.draggableProps}
												{...provided.dragHandleProps}
												component="div"
												className={styles.tableCell}
												sx={{
													minWidth: col.width,
													maxWidth: col.width,
													...provided.draggableProps.style,
												}}
											>
												<Box className={styles.tableCellContainer}>
													<Typography
														className={styles.columnNames}
													>
														{col.title}
													</Typography>
													{(col.enableSearch ||
														col.enableFilter ||
														col.sortable) &&
														renderHeader(col)}
												</Box>
											</TableCell>
										)}
									</Draggable>
								))}
							</TableRow>
							{/* Full-width border line */}
							<Box
								sx={{
									position: 'absolute',
									bottom: 0,
									left: 0,
									right: 0,
									height: '1px',
									backgroundColor: '#B0B0B0',
									width: '100%'
								}}
							/>
						</MuiTableHead>
					)}
				</Droppable>
			</DragDropContext>
		);
	},
);

export default TableHead;
